import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import UniversalModal from '../Common/UniversalModal';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import Toast from '../Common/Toast';
import OrganizerForm from './OrganizerForm';
import Pagination from '../Common/Pagination';
import '../Common/CardActions.css';
import '../Common/EntityCards.css';
import '../Common/PrimaryActionButton.css';
import OwnerBadge from '../Common/OwnerBadge';
import { useAuth } from '../../context/AuthContext';

const OrganizersList = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: null, // 'create', 'view', 'edit'
    organizer: null
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'specialized_org',
    entity_type: 'legal',
    inn: '',
    ogrn: '',
    address: '',
    phone: '',
    email: '',
    mailing_address: '',
    executive_role: 'Генеральный директор',
    executive_name: '',
    contacts: '',
    sro_info: ''
  });
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  
  // Состояние для диалога подтверждения
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена'
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    loadOrganizers();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
          setCurrentPage(1);
        }
      } catch (_) {
        // ignore
      }
    })();
  }, []);

  const loadOrganizers = async () => {
    try {
      setLoading(true);
      setError('');
      const allOrganizers = [];
      let page = 1;
      while (true) {
        const response = await apiService.getOrganizers(`?page=${page}&page_size=96`);
        const chunk = response.data.results || response.data || [];
        allOrganizers.push(...chunk);
        if (!response.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      // Показываем только специализированные организации
      const filtered = allOrganizers.filter(o => o.type === 'specialized_org');
      setOrganizers(filtered);
    } catch (err) {
      console.error('Error loading organizers:', err);
      setError('Ошибка при загрузке организаторов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'specialized_org',
      entity_type: 'legal',
      inn: '',
      ogrn: '',
      address: '',
      phone: '',
      email: '',
      mailing_address: '',
      executive_role: 'Генеральный директор',
      executive_name: '',
      contacts: '',
      sro_info: ''
    });
    setHasUnsavedChanges(false);
  };
  
  const handleCancelModal = () => {
    if (modalState.mode === 'edit' && !hasUnsavedChanges) {
      setModalState({ ...modalState, mode: 'view' });
      setHasUnsavedChanges(false);
      return;
    }
    setModalState({ isOpen: false, mode: null, organizer: null });
    setHasUnsavedChanges(false);
    resetForm();
  };

  const handleAddOrganizer = () => {
    resetForm();
    setModalState({ isOpen: true, mode: 'create', organizer: null });
  };

  const handleViewOrganizer = (organizer) => {
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'view', organizer });
  };

  const handleEditOrganizer = (organizer) => {
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'edit', organizer });
  };
  
  const handleStartEdit = () => {
    setHasUnsavedChanges(false);
    setModalState({ ...modalState, mode: 'edit' });
  };


  const handleDeleteOrganizer = (organizer) => {
    showConfirmDialog(
      'Внимание: каскадное удаление',
      `Вы уверены, что хотите удалить организатора ${organizer.name}?\n\nВажно: вместе с организатором будут удалены карточки торгов, в которых он указан.\nЭто действие нельзя отменить.`,
      async () => {
        try {
          await apiService.deleteOrganizer(organizer.id);
          setOrganizers(organizers.filter(o => o.id !== organizer.id));
        } catch (err) {
          setError('Ошибка при удалении организатора: ' + (err.response?.data?.detail || err.message));
        }
      }
    );
  };

  const handleSaveOrganizer = async (payload, { exit } = {}) => {
    try {
      let savedOrganizer = null;

      if (modalState.organizer) {
        const response = await apiService.updateOrganizer(modalState.organizer.id, { ...payload, type: 'specialized_org' });
        savedOrganizer = response.data || { ...modalState.organizer, ...payload, type: 'specialized_org' };
        setOrganizers(organizers.map(o => o.id === savedOrganizer.id ? savedOrganizer : o));
      } else {
        const response = await apiService.createOrganizer({ ...payload, type: 'specialized_org' });
        savedOrganizer = response.data;
      setOrganizers([savedOrganizer, ...organizers]);
      }

      setHasUnsavedChanges(false);

      showToast(modalState.organizer ? 'Изменения сохранены' : 'Организатор создан');

      if (exit) {
        setModalState({ isOpen: false, mode: null, organizer: null });
        resetForm();
      } else {
        setModalState({ isOpen: true, mode: 'edit', organizer: savedOrganizer });
      }

      return savedOrganizer;
    } catch (err) {
      console.error('Error saving organizer:', err.response?.data || err);
      let errorMessage = 'Ошибка при сохранении организатора: ';

      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage += data;
        } else if (data.detail) {
          errorMessage += data.detail;
        } else if (data.inn) {
          errorMessage += Array.isArray(data.inn) ? data.inn.join(', ') : data.inn;
        } else if (data.name) {
          if (Array.isArray(data.name)) {
            errorMessage += data.name.join(', ');
          } else {
            errorMessage += data.name;
          }
        } else {
          errorMessage += JSON.stringify(data);
        }
      } else {
        errorMessage += err.message;
      }

      setError(errorMessage);
      showToast(errorMessage, 'error');
      return false;
    }
  };

  // Функции для управления диалогом подтверждения
  const showConfirmDialog = (title, message, onConfirm, confirmText = 'Удалить') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText: 'Отмена'
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirmAction = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    hideConfirmDialog();
  };

  // Обработчики пагинации
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении количества элементов
    apiService.saveUiSettings({ table_page_size: newItemsPerPage }).catch(() => {});
  };

  const filtered = React.useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return organizers;
    return (organizers || []).filter((o) => {
      const hay = `${o.name || ''} ${o.inn || ''} ${o.ogrn || ''} ${o.address || ''} ${o.phone || ''} ${o.email || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [organizers, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, organizers]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const duplicateInnMeta = React.useMemo(() => {
    const byInn = new Map();
    (organizers || []).forEach((organizer) => {
      const inn = String(organizer?.inn || '').trim();
      if (!inn) return;
      if (!byInn.has(inn)) {
        byInn.set(inn, { owners: new Set(), count: 0 });
      }
      const meta = byInn.get(inn);
      meta.count += 1;
      const ownerKey = String(organizer?.owner_email || '').trim() || `no-owner-${organizer?.id}`;
      meta.owners.add(ownerKey);
    });
    return byInn;
  }, [organizers]);
  const paged = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Загрузка организаторов...</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>📋 Организаторы торгов</h1>
        <button 
          onClick={handleAddOrganizer}
          className="primary-create-button"
        >
          <span className="primary-create-button__icon">+</span>
          Добавить организатора
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по организаторам, ИНН, ОГРН, адресу..."
          style={{ flex: 1, minWidth: 260, padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
        />
        <div style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
          Найдено: <b>{totalItems}</b>
        </div>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#fee', 
          border: '1px solid #fcc', 
          color: '#a00', 
          padding: '15px', 
          borderRadius: '4px', 
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button 
            onClick={loadOrganizers}
            style={{ 
              padding: '5px 10px', 
              backgroundColor: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            Повторить
          </button>
        </div>
      )}

      <div>
        {paged.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px', 
            color: '#666',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <h3>Организаторы не найдены</h3>
            <p>Добавьте первого организатора через кнопку "Добавить организатора"</p>
          </div>
        ) : (
          <div className="entity-cards-grid">
            {paged.map((organizer) => (
              <div 
                key={organizer.id} 
                className="entity-card clickable"
                onClick={() => handleViewOrganizer(organizer)}
              >
                <div className="entity-card-header">
                  <div className="entity-card-title-block">
                    <h3 className="entity-card-title">{organizer.name}</h3>
                    <OwnerBadge email={organizer.owner_email} />
                  </div>
                  <div className="card-actions">
                    <button
                      type="button"
                      className="card-action-btn card-action-btn--edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditOrganizer(organizer);
                      }}
                      title="Редактировать"
                    >
                      ✎
                    </button>
                    <button 
                      type="button"
                      className="card-action-btn card-action-btn--delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteOrganizer(organizer);
                      }}
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="entity-card-body">
                  {/* Тип скрыт в карточке по требованиям */}
                  
                  {organizer.inn && (
                    <div className="entity-info-row">
                      <span className="entity-label">ИНН:</span>
                      <span className="entity-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{organizer.inn}</span>
                        {(() => {
                          if (!isAdmin) return null;
                          const meta = duplicateInnMeta.get(String(organizer.inn).trim());
                          if (!meta || meta.owners.size < 2) return null;
                          return (
                            <span
                              title={`ИНН используется у ${meta.owners.size} пользователей, записей: ${meta.count}`}
                              style={{
                                fontSize: 12,
                                color: '#8a5a00',
                                background: '#fff3cd',
                                border: '1px solid #ffe69c',
                                borderRadius: 999,
                                padding: '2px 8px',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Дубль у пользователей: {meta.owners.size}
                            </span>
                          );
                        })()}
                      </span>
                    </div>
                  )}
                  {organizer.ogrn && (
                    <div className="entity-info-row">
                      <span className="entity-label">ОГРН:</span>
                      <span className="entity-value">{organizer.ogrn}</span>
                    </div>
                  )}
                  {organizer.address && (
                    <div className="entity-info-row">
                      <span className="entity-label">Адрес:</span>
                      <span className="entity-value">{organizer.address}</span>
                    </div>
                  )}
                  {(organizer.phone || organizer.email) && (
                    <div className="entity-info-row">
                      <span className="entity-label">Контакты:</span>
                      <span className="entity-value">
                        {organizer.phone ? `тел. ${organizer.phone}` : ''}
                        {organizer.phone && organizer.email ? ' · ' : ''}
                        {organizer.email || ''}
                      </span>
                    </div>
                  )}
                  {organizer.mailing_address && (
                    <div className="entity-info-row">
                      <span className="entity-label">Для писем:</span>
                      <span className="entity-value">{organizer.mailing_address}</span>
                    </div>
                  )}
                  {(organizer.executive_role || organizer.executive_name) && (
                    <div className="entity-info-row">
                      <span className="entity-label">ЕИО:</span>
                      <span className="entity-value">{(organizer.executive_role || 'Генеральный директор') + (organizer.executive_name ? ` — ${organizer.executive_name}` : '')}</span>
                    </div>
                  )}
                  
                  {/* Сведения о СРО в разделе "Организаторы" не отображаем:
                      если они когда-либо были заполнены, их нужно редактировать только осознанно в карточке управляющего. */}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Пагинация */}
        {totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>

      {/* Модальное окно для работы с организаторами */}
      <UniversalModal
        isOpen={modalState.isOpen}
        onClose={handleCancelModal}
        title={
          modalState.organizer 
            ? (modalState.mode === 'view' ? `Организатор: ${modalState.organizer.name}` : 'Редактирование организатора')
            : 'Создание организатора'
        }
        isViewMode={modalState.mode === 'view'}
        onEdit={handleStartEdit}
        hasChanges={hasUnsavedChanges}
        onConfirmClose={handleCancelModal}
        modalClassName="universal-modal--wide"
        disableEscClose={confirmDialog.isOpen}
      >
        <OrganizerForm
          organizer={modalState.organizer}
          formData={formData}
          setFormData={setFormData}
          onSave={handleSaveOrganizer}
          onCancel={handleCancelModal}
          isViewMode={modalState.mode === 'view'}
          onFormChange={setHasUnsavedChanges}
          fixedType="specialized_org"
        />
      </UniversalModal>

      {/* Диалог подтверждения */}
      {confirmDialog.isOpen && (
        <ConfirmDialog 
          confirmDialog={confirmDialog}
          onCancel={hideConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
};

// Компонент диалога подтверждения с обработчиком Escape
const ConfirmDialog = ({ confirmDialog, onCancel, onConfirm }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation(); 
        e.stopImmediatePropagation();
        onCancel();
        return false;
      }
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel]);

  return (
    <UnsavedChangesPopover
      isOpen={!!confirmDialog?.isOpen}
      title={confirmDialog?.title || 'Подтверждение'}
      message={confirmDialog?.message}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelText={confirmDialog?.cancelText || 'Отмена'}
      confirmText={confirmDialog?.confirmText || 'Подтвердить'}
      zIndex={10000}
    />
  );
};

export default OrganizersList;
